import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler, PlayerDetailResponse } from '../PlayerHandler';
import PlayerHeader from './PlayerHeader';
import PlayerAchievements from './PlayerAchievements';
import PlayerMatchHistory from './PlayerMatchHistory';
import PlayerStatsSection from './PlayerStatsSection';
import PlayerPurchases from './PlayerPurchases';

const PlayerDetailView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { theme, isMobile } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlayerDetailResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const handler = new PlayerHandler();
        const response = await handler.getPlayerDetails(userId);
        if (response.success && response.playerDetail) {
          setData(response.playerDetail);
        } else {
          setError('Failed to load player details');
        }
      } catch (err) {
        console.error('Error loading player details:', err);
        setError('Failed to load player details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const styles: Record<string, React.CSSProperties> = {
    container: {
      padding: 0,
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      fontSize: '14px',
      fontWeight: 500,
      color: theme.text,
      background: theme.card,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      cursor: 'pointer',
      marginBottom: '20px',
      transition: 'background 0.2s',
    },
    title: {
      fontSize: isMobile ? '22px' : '26px',
      fontWeight: 700,
      color: theme.titleColor,
      marginBottom: '16px',
      textAlign: 'center' as const,
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '80px 0',
      gap: '16px',
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: '3px solid rgba(59, 130, 246, 0.3)',
      borderTop: '3px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    errorContainer: {
      textAlign: 'center' as const,
      padding: '60px 0',
      color: theme.textSecondary,
    },
    errorText: {
      fontSize: '16px',
      marginBottom: '16px',
    },
    retryButton: {
      padding: '8px 20px',
      fontSize: '14px',
      fontWeight: 500,
      color: '#fff',
      background: '#3b82f6',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr',
      gap: '20px',
      marginBottom: '20px',
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0px',
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0px',
    },
    threeColumnGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
      gap: '20px',
    },
    placeholderSection: {
      background: theme.card,
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      marginBottom: '0px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '12px',
    },
    comingSoon: {
      color: theme.textSecondary,
      fontSize: '14px',
      fontStyle: 'italic' as const,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <span style={{ color: theme.textSecondary }}>Loading player details...</span>
        </div>
      </div>
    );
  }

  if (error || !data || !data.header) {
    return (
      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          &#8592; Back to Players
        </button>
        <div style={styles.errorContainer}>
          <div style={styles.errorText}>{error || 'Player not found'}</div>
          <button style={styles.retryButton} onClick={() => navigate(-1)}>
            Back to Players
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

      <button
        style={styles.backButton}
        onClick={() => navigate(-1)}
        onMouseEnter={(e) => { (e.target as HTMLElement).style.background = theme.tableRowHover; }}
        onMouseLeave={(e) => { (e.target as HTMLElement).style.background = theme.card; }}
      >
        &#8592; Back to Players
      </button>

      <h1 style={styles.title}>Player Information</h1>

      <PlayerHeader header={data.header} />

      <div style={styles.mainGrid}>
        <div style={styles.leftColumn}>
          <PlayerAchievements achievements={data.achievements || []} />
          <PlayerMatchHistory userId={userId!} />
        </div>

        <div style={styles.rightColumn}>
          <PlayerStatsSection
            mode="single"
            title="Single Mode"
            singleStats={data.singleModeStats}
          />
          <PlayerStatsSection
            mode="vs"
            title="VS Mode"
            vsStats={data.vsModeStats}
          />
          <PlayerStatsSection
            mode="vsBot"
            title="VS Bot"
            vsStats={data.vsBotStats}
          />
          <PlayerStatsSection
            mode="daily"
            title="Daily Challenge"
            dailyStats={data.dailyChallengeStats}
          />
        </div>
      </div>

      <div style={styles.threeColumnGrid}>
        <PlayerPurchases purchases={data.purchases || []} />
        <div style={styles.placeholderSection}>
          <div style={styles.sectionTitle}>Reports</div>
          <div style={styles.comingSoon}>Coming Soon</div>
        </div>
        <div style={styles.placeholderSection}>
          <div style={styles.sectionTitle}>Feedback</div>
          <div style={styles.comingSoon}>Coming Soon</div>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailView;
